import React, { useState, useEffect } from "react";
import {
  DollarSign,
  ShoppingCart,
  CreditCard,
  TrendingUp,
  Wallet,
  Clock,
  XCircle,
  AlertCircle,
  BarChart3,
  CheckCircle,
  Truck,
  Landmark,
  ShieldCheck,
  Package,
} from "lucide-react";
import * as S from "./dashboardStyle";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import deliveryManApi from "@apis/deliveryman";
import Typography from "@components/typography";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface DeliveryOverviewDashboardProps {
  deliveryManId: number;
}

const DeliveryOverviewDashboard: React.FC<DeliveryOverviewDashboardProps> = ({
  deliveryManId,
}) => {
  const { settings } = useGeneralSettings();
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Daily delivered breakdown (this month) — drives the "Daily Deliveries"
  // section below so the overview shows completed-delivery detail per day.
  const [daily, setDaily] = useState<{ total: number; days: any[] }>({
    total: 0,
    days: [],
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const res = await deliveryManApi.getAnalytics(deliveryManId);
        setAnalyticsData(res);
      } catch (error) {
        console.error("Failed to fetch delivery analytics", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [deliveryManId]);

  useEffect(() => {
    const fetchDaily = async () => {
      try {
        const res = await deliveryManApi.getDailyDelivered(deliveryManId, {
          range: "this_month",
        });
        setDaily({ total: res?.total || 0, days: res?.days || [] });
      } catch (error) {
        console.error("Failed to fetch daily deliveries", error);
        setDaily({ total: 0, days: [] });
      }
    };
    if (deliveryManId) fetchDaily();
  }, [deliveryManId]);

  if (loading) return <div>Loading analytics...</div>;
  if (!analyticsData) return <div>No analytics data available</div>;

  const {
    walletBalance,
    earnings,
    codFinancials,
    onlineFinancials,
    orderOverview,
    transactionOverview,
    withdrawalOverview,
    recentWithdrawals,
  } = analyticsData;

  const totalEarnings = earnings?.total || 0;

  // Daily deliveries chart data — chronological (oldest -> newest) for the bars,
  // with a short axis label and the per-day metrics from the backend.
  const dailyChartData = [...daily.days].reverse().map((d: any) => ({
    label: new Date(d.date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
    delivered: d.count || 0,
    deliveryEarnings: Number(d.deliveryEarnings || 0),
    tips: Number(d.tips || 0),
    codCollected: Number(d.codCollected || 0),
  }));

  // Combine transaction overview
  const totalTransactions =
    (transactionOverview?.credit || 0) + (transactionOverview?.debit || 0);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivered":
      case "completed":
      case "success":
      case "active":
      case "approved":
        return "#10b981";
      case "pending":
      case "order placed":
        return "#f59e0b";
      case "processing":
      case "order processing":
      case "delivery assigned":
        return "#3b82f6";
      case "cancelled":
      case "failed":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  return (
    <S.DashboardContainer>
      {/* Financial Summary */}
      <S.Section>
        <S.SectionTitle>
          <BarChart3 size={20} />
          Financial Summary
        </S.SectionTitle>

        <div style={{ marginBottom: "32px" }}>
          <Typography
            size={12}
            weight={700}
            color="#64748b"
            styleText="text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px; display: flex; align-items: center; gap: 8px;"
          >
            <TrendingUp size={14} />
            Earnings Flow
          </Typography>
          <S.BreakdownGrid>
            <S.BreakdownCard>
              <S.IconContainer color="#10b981">
                <Truck size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Delivery Charges</S.BreakdownLabel>
              <S.BreakdownValue color="#10b981">
                {Helper.formatCurrency(earnings?.deliveryCharge || 0, settings)}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#f59e0b">
                <CheckCircle size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Tips Collected</S.BreakdownLabel>
              <S.BreakdownValue color="#f59e0b">
                {Helper.formatCurrency(earnings?.tips || 0, settings)}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#3b82f6">
                <TrendingUp size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Incentives</S.BreakdownLabel>
              <S.BreakdownValue color="#3b82f6">
                {Helper.formatCurrency(earnings?.incentives || 0, settings)}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#6366f1">
                <CreditCard size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Online Earnings</S.BreakdownLabel>
              <S.BreakdownValue color="#6366f1">
                {Helper.formatCurrency(
                  onlineFinancials?.totalEarnings || 0,
                  settings,
                )}
              </S.BreakdownValue>
            </S.BreakdownCard>
          </S.BreakdownGrid>

          <div style={{ marginTop: "20px" }}>
            <S.BreakdownCard highlight>
              <S.IconContainer color="#ffffff" highlight>
                <DollarSign size={16} />
              </S.IconContainer>
              <S.BreakdownLabel inverted>Total Earnings</S.BreakdownLabel>
              <S.BreakdownValue inverted>
                {Helper.formatCurrency(
                  totalEarnings + (onlineFinancials?.totalEarnings || 0),
                  settings,
                )}
              </S.BreakdownValue>
              <S.BreakdownSubtext inverted>
                Includes Charges, Tips, Incentives & Online Pay
              </S.BreakdownSubtext>
            </S.BreakdownCard>
          </div>
        </div>

        <div>
          <Typography
            size={12}
            weight={700}
            color="#64748b"
            styleText="text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px; display: flex; align-items: center; gap: 8px;"
          >
            <Landmark size={14} />
            Wallet & COD Settlements
          </Typography>
          <S.BreakdownGrid>
            <S.BreakdownCard highlight>
              <S.IconContainer color="#ffffff" highlight>
                <Wallet size={16} />
              </S.IconContainer>
              <S.BreakdownLabel inverted>Total Wallet Balance</S.BreakdownLabel>
              <S.BreakdownValue inverted>
                {Helper.formatCurrency(walletBalance || 0, settings)}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#ef4444">
                <DollarSign size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>COD Cash In Hand</S.BreakdownLabel>
              <S.BreakdownValue color="#ef4444">
                {Helper.formatCurrency(
                  codFinancials?.cashInHand || 0,
                  settings,
                )}
              </S.BreakdownValue>
              <S.BreakdownSubtext>
                Pending Cash:{" "}
                {Helper.formatCurrency(
                  codFinancials?.pendingCash || 0,
                  settings,
                )}
              </S.BreakdownSubtext>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#10b981">
                <ShieldCheck size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Driver Share (COD)</S.BreakdownLabel>
              <S.BreakdownValue color="#10b981">
                {Helper.formatCurrency(
                  codFinancials?.driverShare || 0,
                  settings,
                )}
              </S.BreakdownValue>
              <S.BreakdownSubtext>Retained by Driver</S.BreakdownSubtext>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#f59e0b">
                <AlertCircle size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Payable to Admin</S.BreakdownLabel>
              <S.BreakdownValue color="#f59e0b">
                {Helper.formatCurrency(
                  codFinancials?.payableToAdmin || 0,
                  settings,
                )}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#f59e0b">
                <Clock size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Pending Withdrawal</S.BreakdownLabel>
              <S.BreakdownValue color="#f59e0b">
                {Helper.formatCurrency(
                  withdrawalOverview?.Pending || 0,
                  settings,
                )}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#10b981">
                <ShieldCheck size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Successful Withdrawal</S.BreakdownLabel>
              <S.BreakdownValue color="#10b981">
                {Helper.formatCurrency(
                  withdrawalOverview?.Approved || 0,
                  settings,
                )}
              </S.BreakdownValue>
            </S.BreakdownCard>

            <S.BreakdownCard>
              <S.IconContainer color="#ef4444">
                <XCircle size={16} />
              </S.IconContainer>
              <S.BreakdownLabel>Rejected Withdrawal</S.BreakdownLabel>
              <S.BreakdownValue color="#ef4444">
                {Helper.formatCurrency(
                  withdrawalOverview?.Rejected || 0,
                  settings,
                )}
              </S.BreakdownValue>
            </S.BreakdownCard>
          </S.BreakdownGrid>
        </div>

        <S.BreakdownFooter>
          <Typography size={13} color="#64748b">
            * This summary aggregates financial data from all order types (COD &
            Online).
          </Typography>
        </S.BreakdownFooter>
      </S.Section>

      {/* Withdrawal Overview */}
      <S.Section>
        <S.SectionTitle>
          <Wallet size={20} />
          Withdrawal History
        </S.SectionTitle>
        <S.TableContainer>
          <S.Table>
            <S.TableHeader>
              <S.TableRow>
                <S.TableHead>Amount</S.TableHead>
                <S.TableHead>Status</S.TableHead>
                <S.TableHead>Date</S.TableHead>
                <S.TableHead>Remarks</S.TableHead>
              </S.TableRow>
            </S.TableHeader>
            <S.TableBody>
              {recentWithdrawals && recentWithdrawals.length > 0 ? (
                recentWithdrawals.map((item: any) => (
                  <S.TableRow key={item.id}>
                    <S.TableCell>
                      <S.AmountText positive={false}>
                        {Helper.formatCurrency(item.amount, settings)}
                      </S.AmountText>
                    </S.TableCell>
                    <S.TableCell>
                      {(() => {
                        const isPartiallyPaid =
                          item.status === "Pending" &&
                          (item.paidAmount || 0) > 0;
                        const isPartiallyPaidAndRejected =
                          item.status === "Rejected" &&
                          (item.paidAmount || 0) > 0;

                        let displayStatus = item.status;
                        let colorStatus = item.status;

                        if (isPartiallyPaid) {
                          displayStatus = "Partially Paid";
                          colorStatus = "Approved";
                        } else if (isPartiallyPaidAndRejected) {
                          displayStatus = "Partially Paid & Rejected";
                          colorStatus = "Rejected";
                        }

                        return (
                          <S.StatusBadge color={getStatusColor(colorStatus)}>
                            {/* Icon rendering logic can be duplicated or abstracted if needed, sticking to text/color for now or reusing getStatusColor which returns hex */}
                            {displayStatus}
                          </S.StatusBadge>
                        );
                      })()}
                    </S.TableCell>
                    <S.TableCell>
                      {new Date(item.createdAt).toLocaleDateString()}
                    </S.TableCell>
                    <S.TableCell>
                      <div
                        style={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography size={12} color="#64748b">
                          {item.adminNote || "—"}
                        </Typography>
                      </div>
                    </S.TableCell>
                  </S.TableRow>
                ))
              ) : (
                <S.TableRow>
                  <S.TableCell colSpan={4} style={{ textAlign: "center" }}>
                    No withdrawal history found
                  </S.TableCell>
                </S.TableRow>
              )}
            </S.TableBody>
          </S.Table>
        </S.TableContainer>
      </S.Section>

      {/* Order Overview */}
      <S.Section>
        <S.SectionTitle>
          <ShoppingCart size={20} />
          Delivery Statistics
        </S.SectionTitle>
        <S.StatsGrid>
          {orderOverview && Object.entries(orderOverview).length > 0 ? (
            Object.entries(orderOverview).map(([status, count]: any) => (
              <S.StatItem key={status}>
                <S.StatLabel>{status}</S.StatLabel>
                <S.StatValue color={getStatusColor(status)}>
                  {count}
                </S.StatValue>
              </S.StatItem>
            ))
          ) : (
            <div style={{ padding: "20px", color: "#64748b" }}>
              No delivery statistics available.
            </div>
          )}
        </S.StatsGrid>
      </S.Section>

      {/* Daily Deliveries */}
      <S.Section>
        <S.SectionTitle>
          <Package size={20} />
          Daily Deliveries
        </S.SectionTitle>

        <S.StatsGrid style={{ marginBottom: "20px" }}>
          <S.StatItem>
            <S.StatLabel>Delivered This Month</S.StatLabel>
            <S.StatValue color="#10b981">{daily.total}</S.StatValue>
          </S.StatItem>
          <S.StatItem>
            <S.StatLabel>Active Days</S.StatLabel>
            <S.StatValue color="#3b82f6">{daily.days.length}</S.StatValue>
          </S.StatItem>
          <S.StatItem>
            <S.StatLabel>Avg / Active Day</S.StatLabel>
            <S.StatValue color="#f59e0b">
              {daily.days.length
                ? Math.round((daily.total / daily.days.length) * 10) / 10
                : 0}
            </S.StatValue>
          </S.StatItem>
        </S.StatsGrid>

        {dailyChartData.length > 0 ? (
          <>
            {/* Orders delivered per day */}
            <Typography
              size={12}
              weight={700}
              color="#64748b"
              styleText="text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 12px; display: block;"
            >
              Orders Delivered Per Day
            </Typography>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart
                data={dailyChartData}
                margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <RTooltip
                  formatter={(value: any) => [`${value} orders`, "Delivered"]}
                  cursor={{ fill: "#10b98112" }}
                />
                <Bar
                  dataKey="delivered"
                  name="Delivered"
                  fill="#10b981"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={48}
                />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Earnings & COD per day */}
            <Typography
              size={12}
              weight={700}
              color="#64748b"
              styleText="text-transform: uppercase; letter-spacing: 0.08em; margin: 24px 0 12px; display: block;"
            >
              Earnings &amp; COD Per Day
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart
                data={dailyChartData}
                margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <RTooltip
                  formatter={(value: any, name: any) => [
                    Helper.formatCurrency(Number(value) || 0, settings),
                    name,
                  ]}
                  cursor={{ fill: "#3b82f612" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="deliveryEarnings"
                  name="Delivery Earnings"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                />
                <Bar
                  dataKey="tips"
                  name="Tips"
                  fill="#f59e0b"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={36}
                />
                <Line
                  type="monotone"
                  dataKey="codCollected"
                  name="COD Collected"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "180px",
              color: "#64748b",
            }}
          >
            <Typography size={14} color="#64748b">
              No deliveries this month
            </Typography>
          </div>
        )}
      </S.Section>

      {/* Transaction Overview */}
      <S.Section>
        <S.SectionTitle>
          <CreditCard size={20} />
          Transaction Overview
        </S.SectionTitle>
        <S.MetricsGrid>
          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#6366f1">
                <CreditCard size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>{totalTransactions}</S.MetricValue>
            <S.MetricLabel>Total Transactions</S.MetricLabel>
          </S.MetricCard>

          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#10b981">
                <TrendingUp size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>
              {Helper.formatCurrency(
                transactionOverview?.credit || 0,
                settings,
              )}
            </S.MetricValue>
            <S.MetricLabel>Total Credits</S.MetricLabel>
          </S.MetricCard>

          <S.MetricCard>
            <S.MetricHeader>
              <S.MetricIcon className="metric-icon" bgColor="#ef4444">
                <XCircle size={24} />
              </S.MetricIcon>
            </S.MetricHeader>
            <S.MetricValue>
              {Helper.formatCurrency(transactionOverview?.debit || 0, settings)}
            </S.MetricValue>
            <S.MetricLabel>Total Debits</S.MetricLabel>
          </S.MetricCard>
        </S.MetricsGrid>
      </S.Section>
    </S.DashboardContainer>
  );
};

export default DeliveryOverviewDashboard;
